import React from "react";
import { useTranslation } from "react-i18next";
import "./ContactDetails.css";

/**
 * Contact — Section 05 : coordonnées professionnelles.
 * Ce composant ne publie ni coordonnées fictives ni adresses non validées.
 * Insérer uniquement des valeurs officiellement confirmées dans CONTACT_DETAILS.
 * Le lien email/téléphone/cartographie n'est actif que si sa donnée existe.
 */
const CONTACT_DETAILS = Object.freeze({
  email: "",
  phoneDisplay: "",
  phoneInternational: "",
  location: "",
  locationUrl: "",
});

const COPY = {
  fr: {
    eyebrow: "PARLONS ENSEMBLE",
    title: "Vous préférez échanger directement ?",
    introduction:
      "Retrouvez nos coordonnées professionnelles. Les informations ci-dessous devront être validées avant publication.",
    emailLabel: "Email professionnel",
    emailPlaceholder: "Adresse officielle à confirmer",
    phoneLabel: "Téléphone commercial ou général",
    phonePlaceholder: "Numéro et fonction à confirmer",
    locationLabel: "Implantation",
    locationPlaceholder: "Adresse et statut de l’établissement à confirmer",
    locationLink: "Voir l’itinéraire",
    note: "Aucun numéro, horaire ou délai de réponse n’est annoncé sans vérification.",
  },
  en: {
    eyebrow: "LET’S TALK",
    title: "Would you prefer to speak with us directly?",
    introduction:
      "Find our business contact information here. The details below must be verified before publication.",
    emailLabel: "Business email",
    emailPlaceholder: "Official address to be confirmed",
    phoneLabel: "Business or main telephone",
    phonePlaceholder: "Number and purpose to be confirmed",
    locationLabel: "Location",
    locationPlaceholder: "Address and office status to be confirmed",
    locationLink: "Get directions",
    note: "No phone number, opening hours or response times are stated without verification.",
  },
};

function ContactIcon({ type }) {
  if (type === "email") {
    return (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
        <rect x="3.25" y="5.5" width="17.5" height="13" rx="2.4" stroke="currentColor" strokeWidth="1.65" />
        <path d="m4.5 7 7.5 6 7.5-6" stroke="currentColor" strokeWidth="1.65" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  if (type === "phone") {
    return (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
        <path d="M7.3 4.4 10 7.1 8.5 9.3c1.1 2.5 3 4.4 5.6 5.6l2.2-1.5 2.7 2.7-2 3.1c-.5.7-1.5 1-2.3.8C9.8 19 5 14.3 3.6 7.8c-.2-.9.1-1.8.8-2.3l2.9-1.1Z" stroke="currentColor" strokeWidth="1.55" strokeLinejoin="round" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
      <path d="M19.2 10c0 5.6-7.2 10-7.2 10S4.8 15.6 4.8 10a7.2 7.2 0 1 1 14.4 0Z" stroke="currentColor" strokeWidth="1.65" />
      <circle cx="12" cy="10" r="2.6" stroke="currentColor" strokeWidth="1.65" />
    </svg>
  );
}

export default function ContactDetails() {
  const { i18n } = useTranslation();
  const language = (i18n.resolvedLanguage || i18n.language || "fr")
    .split("-")[0]
    .toLowerCase();
  const content = COPY[language] || COPY.fr;
  const { email, phoneDisplay, phoneInternational, location, locationUrl } = CONTACT_DETAILS;

  return (
    <aside className="contact-details" aria-labelledby="contact-details-title">
      <div className="contact-details__heading">
        <p className="contact-details__eyebrow">{content.eyebrow}</p>
        <h2 id="contact-details-title" className="contact-details__title">
          {content.title}
        </h2>
        <p className="contact-details__introduction">{content.introduction}</p>
      </div>

      <dl className="contact-details__list">
        <div className="contact-details__item">
          <dt className="contact-details__term">
            <span className="contact-details__icon"><ContactIcon type="email" /></span>
            <span className="contact-details__label">{content.emailLabel}</span>
          </dt>
          <dd className="contact-details__itemContent">
            {email ? (
              <a className="contact-details__link" href={`mailto:${email}`}>{email}</a>
            ) : (
              <span className="contact-details__placeholder">{content.emailPlaceholder}</span>
            )}
          </dd>
        </div>

        <div className="contact-details__item">
          <dt className="contact-details__term">
            <span className="contact-details__icon"><ContactIcon type="phone" /></span>
            <span className="contact-details__label">{content.phoneLabel}</span>
          </dt>
          <dd className="contact-details__itemContent">
            {phoneDisplay ? (
              phoneInternational ? (
                <a className="contact-details__link" href={`tel:${phoneInternational}`}>{phoneDisplay}</a>
              ) : (
                <span className="contact-details__value">{phoneDisplay}</span>
              )
            ) : (
              <span className="contact-details__placeholder">{content.phonePlaceholder}</span>
            )}
          </dd>
        </div>

        <div className="contact-details__item">
          <dt className="contact-details__term">
            <span className="contact-details__icon"><ContactIcon type="location" /></span>
            <span className="contact-details__label">{content.locationLabel}</span>
          </dt>
          <dd className="contact-details__itemContent">
            {location ? (
              <>
                <span className="contact-details__value">{location}</span>
                {locationUrl && (
                  <a className="contact-details__link contact-details__mapLink" href={locationUrl} target="_blank" rel="noopener noreferrer">
                    {content.locationLink}
                    <span aria-hidden="true">↗</span>
                  </a>
                )}
              </>
            ) : (
              <span className="contact-details__placeholder">{content.locationPlaceholder}</span>
            )}
          </dd>
        </div>
      </dl>

      <p className="contact-details__note">{content.note}</p>
    </aside>
  );
}
